/**
 * Централизованный кэш для прав пользователя
 * Используется всеми провайдерами и хуками для избежания дублирования запросов
 */

import { resolveApiUrl } from '@/providers/api-url';
import { ALL_PERMISSIONS, LEGACY_PERMISSION_MAP } from '@masters-club/types';

const API_URL = resolveApiUrl();

/**
 * Транзитивное замыкание implies: ключ → набор всех implied кодов.
 * DFS с защитой от циклов. Строится один раз lazily.
 * Зеркалирует backend hasPermissionWithWildcard логику (permission.guard.ts).
 */
let impliesClosureCache: Map<string, Set<string>> | null = null;

function buildImpliesClosure(): Map<string, Set<string>> {
  const direct = new Map<string, string[]>();
  for (const def of ALL_PERMISSIONS) {
    if (def.implies && def.implies.length > 0) {
      direct.set(def.code, def.implies);
    }
  }

  const closure = new Map<string, Set<string>>();

  function expand(code: string, visited: Set<string>): Set<string> {
    if (closure.has(code)) {
      return closure.get(code) as Set<string>;
    }
    if (visited.has(code)) {
      return new Set();
    }
    visited.add(code);

    const result = new Set<string>();
    const directList = direct.get(code) ?? [];
    for (const implied of directList) {
      result.add(implied);
      const nested = expand(implied, visited);
      for (const n of nested) {
        result.add(n);
      }
    }

    closure.set(code, result);
    return result;
  }

  for (const def of ALL_PERMISSIONS) {
    expand(def.code, new Set());
  }

  return closure;
}

function getImpliesClosure(): Map<string, Set<string>> {
  if (!impliesClosureCache) {
    impliesClosureCache = buildImpliesClosure();
  }
  return impliesClosureCache;
}

// Кэш прав
let permissionsCache: string[] | null = null;
let cacheExpiry: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 минут

// Дедупликация запросов в полёте
let pendingRequest: Promise<string[]> | null = null;

/**
 * Очистить кэш прав (вызывать при logout или смене роли)
 */
export function clearPermissionsCache(): void {
  permissionsCache = null;
  cacheExpiry = 0;
  pendingRequest = null;
}

/**
 * Загружает права пользователя с кэшированием и дедупликацией
 * Использует /auth/admin/me который не требует специальных прав
 */
export async function loadPermissions(): Promise<string[]> {
  const now = Date.now();

  // Возвращаем кэш если он валиден
  if (permissionsCache && cacheExpiry > now) {
    return permissionsCache;
  }

  // Если запрос уже идёт, ждём его результата
  if (pendingRequest) {
    return pendingRequest;
  }

  const accessToken = localStorage.getItem('accessToken');

  if (!accessToken) {
    return [];
  }

  // Создаём новый запрос с дедупликацией
  // Глобальный interceptor автоматически добавляет Authorization header
  // и обрабатывает 401 (refresh + retry)
  pendingRequest = (async () => {
    try {
      const response = await fetch(`${API_URL}/auth/admin/me`);

      if (!response.ok) {
        return [];
      }

      interface MeResponse {
        success: boolean;
        data: {
          id: number;
          email: string;
          roles: string[];
          permissions: string[];
          createdAt: string;
        } | null;
      }

      const json: MeResponse = await response.json();

      if (!json.success || !json.data) {
        return [];
      }

      // Кэшируем результат
      permissionsCache = json.data.permissions;
      cacheExpiry = Date.now() + CACHE_TTL;

      return permissionsCache;
    } catch {
      return [];
    } finally {
      pendingRequest = null;
    }
  })();

  return pendingRequest;
}

/**
 * Получить кэшированные права синхронно (если есть)
 */
export function getCachedPermissions(): string[] | null {
  const now = Date.now();
  if (permissionsCache && cacheExpiry > now) {
    return permissionsCache;
  }
  return null;
}

/**
 * Проверяет право с wildcards, implies и legacy-алиасами (write → manage).
 * Зеркалирует backend hasPermissionWithWildcard.
 * @param permissions коды роли
 * @param requiredCode код, который требует UI
 * @returns true если доступ есть
 * @see docs/epoch/course-dates-rbac-403#20260818-112500/00-brief.md
 * @see docs/epoch/course-dates-rbac-403#20260818-112500/02-research.md
 * @epoch course-dates-rbac-403#20260818-112500
 */
export function hasPermission(permissions: string[], requiredCode: string): boolean {
  // Super admin имеет все права
  if (permissions.includes('*') || permissions.includes('admin.*')) {
    return true;
  }

  const canonicalRequired = LEGACY_PERMISSION_MAP[requiredCode] ?? requiredCode;
  const held = new Set(permissions);
  for (const code of permissions) {
    const canonicalHeld = LEGACY_PERMISSION_MAP[code];
    if (canonicalHeld) {
      held.add(canonicalHeld);
    }
  }

  // Точное совпадение, включая legacy → канон
  if (held.has(requiredCode) || held.has(canonicalRequired)) {
    return true;
  }

  // Wildcard по категории
  const parts = requiredCode.split('.');
  for (let i = parts.length - 1; i > 0; i--) {
    const wildcardCode = parts.slice(0, i).join('.') + '.*';
    if (permissions.includes(wildcardCode)) {
      return true;
    }
  }

  // Транзитивные implies: если у юзера есть право X и closure(X) содержит requiredCode → true
  const closure = getImpliesClosure();
  for (const userPerm of held) {
    const implied = closure.get(userPerm);
    if (implied && (implied.has(requiredCode) || implied.has(canonicalRequired))) {
      return true;
    }
  }

  return false;
}
